import { ColumnState } from "ag-grid-community";
import { create } from "zustand";
import { formatDate } from "src/utils/string/stringUtil";
import { showWarning } from "src/utils/toast/toastUtil";
import {
    HRM3040_R01ServiceAllowDto, HRM3040_R01PositionAllowDto, HRM3040_R01PreviewDto,
    defaultHRM3040_R01ServiceAllowDto, defaultHRM3040_R01PositionAllowDto, defaultHRM3040_R01PreviewDto,
} from "./Dto_HRM3040_R01";
import { HRM3040_R01GridType } from "./Util_HRM3040_R01";

/** 기준 조회와 재직자 미리보기에 사용하는 검색 조건이다. */
export interface HRM3040_R01Filter {
    baseDt: string; // YYYY-MM-DD 기준일자
    scope: string; // cur 기준일 적용분, 빈 값 전체
    compCd: string; // 미리보기 사업장
    orgCd: string; // 미리보기 부서
}
/** 현재 검색 조건의 초기값이다. initData에서는 기준일을 그날의 오늘로 다시 계산한다. */
export const defaultHRM3040_R01Filter: HRM3040_R01Filter = { baseDt: formatDate(new Date()), scope: 'cur', compCd: '', orgCd: '' };
/** 두 편집 그리드 행이 공통 헬퍼에 전달할 수 있는 데이터 모양이다. */
type HRM3040_R01EditRow = HRM3040_R01ServiceAllowDto | HRM3040_R01PositionAllowDto;

/** 필터·대상 그리드·세 목록·선택/삭제·배치·스크롤 상태이다. API 호출은 Content가 담당한다. */
export interface Store_HRM3040_R01 {
    HRM3040_R01Filter: HRM3040_R01Filter; // 현재 입력 조건
    HRM3040_R01PastFilter: HRM3040_R01Filter; // 마지막 성공 조회 조건. 조회 전 기준일은 빈 값
    setHRM3040_R01Filter: (filter: Partial<HRM3040_R01Filter>) => void; // 현재 조건 병합
    setHRM3040_R01PastFilter: (filter: Partial<HRM3040_R01Filter>) => void; // 직전 조건 병합
    activeGridType: HRM3040_R01GridType; // 등록·삭제 대상 그리드
    setActiveGridType: (gridType: HRM3040_R01GridType) => void; // 같은 대상이면 상태 유지
    HRM3040_R01ServiceList: HRM3040_R01ServiceAllowDto[]; // 근속 편집 목록
    setHRM3040_R01ServiceList: (list: HRM3040_R01ServiceAllowDto[]) => void; // 조회 목록 저장
    insertHRM3040_R01ServiceList: (data: HRM3040_R01ServiceAllowDto[], index?: number) => void; // 기준 행 뒤 또는 끝에 삽입
    removeHRM3040_R01ServiceList: (index: number) => void; // valueIndex 신규 행 제거
    updateHRM3040_R01ServiceList: (index: number, data: HRM3040_R01ServiceAllowDto) => void; // 편집한 행 교체
    selectedHRM3040_R01ServiceList: string[]; // 선택 valueIndex 문자열
    addSelectedHRM3040_R01ServiceList: (ids: string[]) => void; // 전달한 식별값으로 선택 교체
    removeSelectedHRM3040_R01ServiceList: (ids?: string[]) => void; // 일부 또는 전체 선택 해제
    initSelectedHRM3040_R01ServiceList: () => void; // 선택 초기화
    allSelectedHRM3040_R01ServiceList: () => void; // 전체 선택
    deletedHRM3040_R01ServiceList: string[]; // 삭제 표시 valueIndex 문자열
    addDeletedHRM3040_R01ServiceList: (ids: string[]) => void; // 삭제 표시 추가
    removeDeletedHRM3040_R01ServiceList: (ids?: string[]) => void; // 일부 또는 전체 삭제 표시 복구
    originalHRM3040_R01ServiceList: HRM3040_R01ServiceAllowDto[]; // 조회 원본
    setOriginalHRM3040_R01ServiceList: (list: HRM3040_R01ServiceAllowDto[]) => void; // 원본 목록 저장
    HRM3040_R01PositionList: HRM3040_R01PositionAllowDto[]; // 직책 편집 목록
    setHRM3040_R01PositionList: (list: HRM3040_R01PositionAllowDto[]) => void; // 조회 목록 저장
    insertHRM3040_R01PositionList: (data: HRM3040_R01PositionAllowDto[], index?: number) => void; // 기준 행 뒤 또는 끝에 삽입
    removeHRM3040_R01PositionList: (index: number) => void; // valueIndex 신규 행 제거
    updateHRM3040_R01PositionList: (index: number, data: HRM3040_R01PositionAllowDto) => void; // 편집한 행 교체
    selectedHRM3040_R01PositionList: string[]; // 선택 valueIndex 문자열
    addSelectedHRM3040_R01PositionList: (ids: string[]) => void; // 전달한 식별값으로 선택 교체
    removeSelectedHRM3040_R01PositionList: (ids?: string[]) => void; // 일부 또는 전체 선택 해제
    initSelectedHRM3040_R01PositionList: () => void; // 선택 초기화
    allSelectedHRM3040_R01PositionList: () => void; // 전체 선택
    deletedHRM3040_R01PositionList: string[]; // 삭제 표시 valueIndex 문자열
    addDeletedHRM3040_R01PositionList: (ids: string[]) => void; // 삭제 표시 추가
    removeDeletedHRM3040_R01PositionList: (ids?: string[]) => void; // 일부 또는 전체 삭제 표시 복구
    originalHRM3040_R01PositionList: HRM3040_R01PositionAllowDto[]; // 조회 원본
    setOriginalHRM3040_R01PositionList: (list: HRM3040_R01PositionAllowDto[]) => void; // 원본 목록 저장
    HRM3040_R01PreviewList: HRM3040_R01PreviewDto[]; // 서버 계산 미리보기
    setHRM3040_R01PreviewList: (list: HRM3040_R01PreviewDto[]) => void; // 미리보기 교체
    serviceColumnState: ColumnState[] | null; // service 컬럼 배치
    setServiceColumnState: (state: ColumnState[] | null) => void; // 컬럼 배치 저장
    serviceScrollTop: number | null; // service 스크롤 위치
    setServiceScrollTop: (top: number | null) => void; // 스크롤 저장
    positionColumnState: ColumnState[] | null; // position 컬럼 배치
    setPositionColumnState: (state: ColumnState[] | null) => void; // 컬럼 배치 저장
    positionScrollTop: number | null; // position 스크롤 위치
    setPositionScrollTop: (top: number | null) => void; // 스크롤 저장
    previewColumnState: ColumnState[] | null; // preview 컬럼 배치
    setPreviewColumnState: (state: ColumnState[] | null) => void; // 컬럼 배치 저장
    previewScrollTop: number | null; // preview 스크롤 위치
    setPreviewScrollTop: (top: number | null) => void; // 스크롤 저장
    initData: () => void; // 모든 화면 상태 초기화
}

/** 서버 목록에 기본값과 누락된 인덱스를 채워 같은 행 타입 T의 목록을 반환한다. */
const buildGridList = <T extends { valueIndex: number; orderIndex: number; orderIndexSeq: number; inserted?: boolean; isModified?: boolean }>(list: T[], defaults: T): T[] =>
    list.map((item, index) => ({
        // 펼치기: 기본 필드 뒤에 응답을 덮고, 삼항 연산자로 기존의 유효한 인덱스는 보존한다.
        ...defaults, ...item, valueIndex: item.valueIndex > 0 ? item.valueIndex : index + 1,
        orderIndex: item.orderIndex > 0 ? item.orderIndex : index + 1, orderIndexSeq: item.orderIndexSeq > 0 ? item.orderIndexSeq : 0,
        // 서버 Boolean null이 기본 false를 덮으면 공통 스위치가 기존 행 수정을 인식하지 못하므로 true만 신규·수정으로 보존한다.
        // 조회 전용 사원 DTO에는 편집 상태 필드를 추가하지 않는다.
        ...('inserted' in defaults ? { inserted: item.inserted === true, isModified: item.isModified === true } : {}),
    }));
/** 조회 원본의 인덱스를 새로 매겨 같은 행 타입 T의 비교 목록을 반환한다. */
const buildOriginalList = <T extends HRM3040_R01EditRow>(list: T[], defaults: T): T[] =>
    list.map((item, index) => ({ ...defaults, ...item, valueIndex: index + 1, orderIndex: index + 1, orderIndexSeq: 0, inserted: false, isModified: false }));
/** 신규 행을 index 뒤 또는 목록 끝에 삽입하고 고유한 인덱스를 부여해 같은 타입 T의 목록을 반환한다. */
const insertGridRows = <T extends HRM3040_R01EditRow>(currentList: T[], data: T[], index?: number): T[] => {
    const list = [...currentList]; // 삽입할 복사 목록
    const target = list.find(item => item.valueIndex === index); // + 버튼의 기준 행
    const maxIndex = Math.max(0, ...list.map(item => item.valueIndex)); // 행 고유값의 현재 최댓값
    // ?.·??: 기준 행이 없으면 마지막 행 뒤의 정렬값으로 등록한다.
    const orderIndex = target?.orderIndex ?? Math.max(0, ...list.map(item => item.orderIndex)) + 1; // 정렬 기준값
    const orderIndexSeq = (target?.orderIndexSeq ?? 0) + 1; // 기준 행 바로 다음 순서
    const newRows = data.map((item, offset) => ({ ...item, valueIndex: maxIndex + offset + 1, orderIndex, orderIndexSeq })); // 삽입 행
    if (index !== undefined && target) list.splice(list.findIndex(item => item.valueIndex === index) + 1, 0, ...newRows);
    else list.push(...newRows);
    return list;
};
/** 행을 교체하기 전에 음수 년수·금액을 경고 후 0으로 보정한다. T는 두 편집 행 중 전달한 타입을 보존한다. */
const updateGridRow = <T extends HRM3040_R01EditRow>(list: T[], index: number, data: T): T[] => {
    const updated = { ...data }; // 수정할 행의 복사본
    if (updated.allowAmt < 0) { showWarning('0보다 작을 수 없습니다.'); updated.allowAmt = 0; }
    if ('fromYear' in updated && updated.fromYear < 0) { showWarning('0보다 작을 수 없습니다.'); updated.fromYear = 0; }
    if ('toYear' in updated && updated.toYear < 0) { showWarning('0보다 작을 수 없습니다.'); updated.toYear = 0; }
    return list.map(item => item.valueIndex === index ? updated : item);
};
/** ids가 있으면 문자열로 변환해 해당 값만 제거하고, 없으면 빈 식별값 목록을 반환한다. */
const removeIds = (current: string[], ids?: string[]): string[] => ids ? current.filter(id => !ids.map(String).includes(id)) : [];

/** HRM3040 화면 상태와 각 상태 변경 함수를 제공한다. */
export const useHRM3040_R01Store = create<Store_HRM3040_R01>((set) => ({
    HRM3040_R01Filter: { ...defaultHRM3040_R01Filter },
    HRM3040_R01PastFilter: { ...defaultHRM3040_R01Filter, baseDt: '' },
    /** 현재 검색 조건 중 전달한 필드만 바꾼다. */
    setHRM3040_R01Filter: filter => set(state => ({ HRM3040_R01Filter: { ...state.HRM3040_R01Filter, ...filter } })),
    /** 성공한 조회 조건을 저장한다. */
    setHRM3040_R01PastFilter: filter => set(state => ({ HRM3040_R01PastFilter: { ...state.HRM3040_R01PastFilter, ...filter } })),
    activeGridType: 'service',
    /** 같은 그리드를 다시 누를 때 컬럼 재생성으로 + 클릭이 끊기지 않게 상태를 유지한다. */
    setActiveGridType: activeGridType => set(state => state.activeGridType === activeGridType ? state : { activeGridType }),
    HRM3040_R01ServiceList: [],
    /** 근속 조회 목록의 기본값과 인덱스를 채운다. */
    setHRM3040_R01ServiceList: list => set({ HRM3040_R01ServiceList: buildGridList(list, defaultHRM3040_R01ServiceAllowDto) }),
    /** 근속 신규 행을 삽입한다. */
    insertHRM3040_R01ServiceList: (data, index) => set(state => ({ HRM3040_R01ServiceList: insertGridRows(state.HRM3040_R01ServiceList, data, index) })),
    /** 근속 신규 행과 그 행의 선택·삭제 식별값을 함께 제거한다. */
    removeHRM3040_R01ServiceList: index => set(state => ({
        HRM3040_R01ServiceList: state.HRM3040_R01ServiceList.filter(row => row.valueIndex !== index),
        selectedHRM3040_R01ServiceList: state.selectedHRM3040_R01ServiceList.filter(id => id !== String(index)),
        deletedHRM3040_R01ServiceList: state.deletedHRM3040_R01ServiceList.filter(id => id !== String(index)),
    })),
    /** 근속 편집 행을 교체하며 음수 값을 보정한다. */
    updateHRM3040_R01ServiceList: (index, data) => set(state => ({ HRM3040_R01ServiceList: updateGridRow(state.HRM3040_R01ServiceList, index, data) })),
    selectedHRM3040_R01ServiceList: [],
    /** 선택 식별값을 문자열로 저장하며 누적하지 않고 교체한다. */
    addSelectedHRM3040_R01ServiceList: ids => set({ selectedHRM3040_R01ServiceList: ids.map(String) }),
    /** 지정한 선택만 또는 전체 선택을 해제한다. */
    removeSelectedHRM3040_R01ServiceList: ids => set(state => ({ selectedHRM3040_R01ServiceList: removeIds(state.selectedHRM3040_R01ServiceList, ids) })),
    /** 근속 선택을 비운다. */
    initSelectedHRM3040_R01ServiceList: () => set({ selectedHRM3040_R01ServiceList: [] }),
    /** 근속 행의 모든 식별값을 문자열로 선택한다. */
    allSelectedHRM3040_R01ServiceList: () => set(state => ({ selectedHRM3040_R01ServiceList: state.HRM3040_R01ServiceList.map(row => String(row.valueIndex)) })),
    deletedHRM3040_R01ServiceList: [],
    /** 삭제 식별값을 문자열로 통일하고 중복 없이 추가한다. */
    addDeletedHRM3040_R01ServiceList: ids => set(state => ({ deletedHRM3040_R01ServiceList: Array.from(new Set([...state.deletedHRM3040_R01ServiceList, ...ids.map(String)])) })),
    /** 지정한 삭제 표시만 또는 전체 삭제 표시를 복구한다. */
    removeDeletedHRM3040_R01ServiceList: ids => set(state => ({ deletedHRM3040_R01ServiceList: removeIds(state.deletedHRM3040_R01ServiceList, ids) })),
    originalHRM3040_R01ServiceList: [],
    /** 근속 조회 원본에 비교용 인덱스를 부여해 저장한다. */
    setOriginalHRM3040_R01ServiceList: list => set({ originalHRM3040_R01ServiceList: buildOriginalList(list, defaultHRM3040_R01ServiceAllowDto) }),
    HRM3040_R01PositionList: [],
    /** 직책 조회 목록의 기본값과 인덱스를 채운다. */
    setHRM3040_R01PositionList: list => set({ HRM3040_R01PositionList: buildGridList(list, defaultHRM3040_R01PositionAllowDto) }),
    /** 직책 신규 행을 삽입한다. */
    insertHRM3040_R01PositionList: (data, index) => set(state => ({ HRM3040_R01PositionList: insertGridRows(state.HRM3040_R01PositionList, data, index) })),
    /** 직책 신규 행과 그 행의 선택·삭제 식별값을 함께 제거한다. */
    removeHRM3040_R01PositionList: index => set(state => ({
        HRM3040_R01PositionList: state.HRM3040_R01PositionList.filter(row => row.valueIndex !== index),
        selectedHRM3040_R01PositionList: state.selectedHRM3040_R01PositionList.filter(id => id !== String(index)),
        deletedHRM3040_R01PositionList: state.deletedHRM3040_R01PositionList.filter(id => id !== String(index)),
    })),
    /** 직책 편집 행을 교체하며 음수 값을 보정한다. */
    updateHRM3040_R01PositionList: (index, data) => set(state => ({ HRM3040_R01PositionList: updateGridRow(state.HRM3040_R01PositionList, index, data) })),
    selectedHRM3040_R01PositionList: [],
    /** 선택 식별값을 문자열로 저장하며 누적하지 않고 교체한다. */
    addSelectedHRM3040_R01PositionList: ids => set({ selectedHRM3040_R01PositionList: ids.map(String) }),
    /** 지정한 선택만 또는 전체 선택을 해제한다. */
    removeSelectedHRM3040_R01PositionList: ids => set(state => ({ selectedHRM3040_R01PositionList: removeIds(state.selectedHRM3040_R01PositionList, ids) })),
    /** 직책 선택을 비운다. */
    initSelectedHRM3040_R01PositionList: () => set({ selectedHRM3040_R01PositionList: [] }),
    /** 직책 행의 모든 식별값을 문자열로 선택한다. */
    allSelectedHRM3040_R01PositionList: () => set(state => ({ selectedHRM3040_R01PositionList: state.HRM3040_R01PositionList.map(row => String(row.valueIndex)) })),
    deletedHRM3040_R01PositionList: [],
    /** 삭제 식별값을 문자열로 통일하고 중복 없이 추가한다. */
    addDeletedHRM3040_R01PositionList: ids => set(state => ({ deletedHRM3040_R01PositionList: Array.from(new Set([...state.deletedHRM3040_R01PositionList, ...ids.map(String)])) })),
    /** 지정한 삭제 표시만 또는 전체 삭제 표시를 복구한다. */
    removeDeletedHRM3040_R01PositionList: ids => set(state => ({ deletedHRM3040_R01PositionList: removeIds(state.deletedHRM3040_R01PositionList, ids) })),
    originalHRM3040_R01PositionList: [],
    /** 직책 조회 원본에 비교용 인덱스를 부여해 저장한다. */
    setOriginalHRM3040_R01PositionList: list => set({ originalHRM3040_R01PositionList: buildOriginalList(list, defaultHRM3040_R01PositionAllowDto) }),
    HRM3040_R01PreviewList: [],
    /** 서버가 계산한 미리보기에 그리드 인덱스를 채운다. */
    setHRM3040_R01PreviewList: list => set({ HRM3040_R01PreviewList: buildGridList(list, defaultHRM3040_R01PreviewDto) }),
    serviceColumnState: null,
    /** service 그리드 컬럼 배치를 저장한다. */
    setServiceColumnState: serviceColumnState => set({ serviceColumnState }),
    serviceScrollTop: null,
    /** service 그리드 스크롤 위치를 저장한다. */
    setServiceScrollTop: serviceScrollTop => set({ serviceScrollTop }),
    positionColumnState: null,
    /** position 그리드 컬럼 배치를 저장한다. */
    setPositionColumnState: positionColumnState => set({ positionColumnState }),
    positionScrollTop: null,
    /** position 그리드 스크롤 위치를 저장한다. */
    setPositionScrollTop: positionScrollTop => set({ positionScrollTop }),
    previewColumnState: null,
    /** preview 그리드 컬럼 배치를 저장한다. */
    setPreviewColumnState: previewColumnState => set({ previewColumnState }),
    previewScrollTop: null,
    /** preview 그리드 스크롤 위치를 저장한다. */
    setPreviewScrollTop: previewScrollTop => set({ previewScrollTop }),
    /** 조회 전 상태로 모든 목록·선택·삭제·원본·배치·스크롤·필터를 돌린다. 재조회하지 않는다. */
    initData: () => set({
        HRM3040_R01Filter: { ...defaultHRM3040_R01Filter, baseDt: formatDate(new Date()) },
        HRM3040_R01PastFilter: { ...defaultHRM3040_R01Filter, baseDt: '' },
        activeGridType: 'service',
        HRM3040_R01ServiceList: [], selectedHRM3040_R01ServiceList: [], deletedHRM3040_R01ServiceList: [], originalHRM3040_R01ServiceList: [],
        HRM3040_R01PositionList: [], selectedHRM3040_R01PositionList: [], deletedHRM3040_R01PositionList: [], originalHRM3040_R01PositionList: [],
        HRM3040_R01PreviewList: [],
        serviceColumnState: null, positionColumnState: null, previewColumnState: null,
        serviceScrollTop: null, positionScrollTop: null, previewScrollTop: null,
    }),
}));
